// Step 11: バックアップファイルのインポートを復元
import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import 'dayjs/locale/ja';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Mantine CSS
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

// Custom styles
import '../css/scrollbar.css';
import '../css/modal-overlay.css';
import '../css/glassmorphism.css';

// Auth & Context
import { AuthProvider } from '@/features/auth/contexts/AuthContext';
import { SidebarProvider } from '@/features/layout/contexts/SidebarContext';
import { NFCProvider } from '@/features/nfc/contexts/NFCContext';
import { GlassmorphismProvider } from '@/contexts/GlassmorphismContext';

// Components
import { LoginPageWithErrorBoundary as Login } from '@/features/auth/components/LoginPageWithErrorBoundary';
import { NFCLoginHandler } from '@/shared/components/nfc/NFCLoginHandler';
import { CustomerNFCHandler } from '@/shared/components/nfc/CustomerNFCHandler';
import { AndroidNFCScanButton } from '@/shared/components/nfc/AndroidNFCScanButton';
import { ErrorBoundaryWrapper } from '@/shared/components/common/ErrorBoundaryWrapper';
import { ThemeProvider } from '@/features/theme/components/ThemeProvider';
import { DevToolsProvider } from '@/providers/DevToolsProvider';
import { PrivateRoute } from '@/shared/components/common/PrivateRoute';
import { MainLayout } from '@/features/layout/components/MainLayout';
import { PublicLayout } from '@/features/layout/components/PublicLayout';
import { LoadingComponent } from '@/shared/components/common/LoadingComponent';
import { EnhancedDashboard } from '@/features/dashboard/pages/EnhancedDashboard';

// Utils
import { initializeDebugTools } from '@/utils/debug';

// Lazy loaded components for better performance
// Store Management - V2 implementation
const StoreListPage = lazy(() => import('@/features/stores/pages/index'));
// const StoreManagement = lazy(() => import('@/features/stores/pages/StoreManagementV2'));
const StoreDetail = lazy(() => import('@/features/stores/pages/detail'));
const StoreNew = lazy(() => import('@/features/stores/pages/new'));
const StoreEdit = lazy(() => import('@/features/stores/pages/edit'));

// Staff Management - V3 implementation (新実装)
const StaffManagement = lazy(() => import('@/features/staff/pages/index'));
const StaffDetail = lazy(() => import('@/features/staff/pages/detail'));
const StaffEdit = lazy(() => import('@/features/staff/pages/edit'));
const StaffNew = lazy(() => import('@/features/staff/pages/new'));

// Customer Management - V3 implementation (新実装)
const CustomerManagement = lazy(() => import('@/features/customers/pages/index'));
const CustomerDetail = lazy(() => import('@/features/customers/pages/CustomerDetail'));
const CustomerEdit = lazy(() => import('@/features/customers/pages/CustomerEdit'));
const CustomerNew = lazy(() => import('@/features/customers/pages/CustomerNew'));
const CustomerRejoin = lazy(() => import('@/features/customers/pages/CustomerRejoin'));
const CustomerPreRegistrationList = lazy(
  () => import('@/features/customer-pre-registrations/pages/CustomerPreRegistrationList')
);
const CustomerPreRegistrationDetail = lazy(
  () => import('@/features/customer-pre-registrations/pages/CustomerPreRegistrationDetail')
);
const CustomerPreRegistrationEdit = lazy(
  () => import('@/features/customer-pre-registrations/pages/CustomerPreRegistrationEdit')
);
const CustomerPreRegistrationPublicForm = lazy(
  () => import('@/features/customer-pre-registrations/pages/CustomerPreRegistrationPublicForm')
);

// School Management - V3 implementation (新実装)
const SchoolManagement = lazy(() => import('@/features/schools/pages/index'));
const SchoolDetail = lazy(() => import('@/features/schools/pages/detail'));
const SchoolEdit = lazy(() => import('@/features/schools/pages/edit'));
const SchoolNew = lazy(() => import('@/features/schools/pages/new'));

// Program Category Management - V3 implementation
const ProgramCategoryManagement = lazy(() => import('@/features/programCategories/pages'));
const ProgramCategoryDetail = lazy(() => import('@/features/programCategories/pages/detail'));
const ProgramCategoryEdit = lazy(() => import('@/features/programCategories/pages/edit'));
const ProgramCategoryNew = lazy(() => import('@/features/programCategories/pages/new'));

// Grade Management - V3 implementation
const GradeManagement = lazy(() => import('@/features/grades/pages'));
const GradeDetail = lazy(() => import('@/features/grades/pages/detail'));
const GradeEdit = lazy(() => import('@/features/grades/pages/edit'));
const GradeNew = lazy(() => import('@/features/grades/pages/new'));

// Program Management - V2 implementation
const ProgramManagement = lazy(() => import('@/features/programs/pages/index'));
const ProgramNew = lazy(() => import('@/features/programs/pages/ProgramNew'));
const ProgramEdit = lazy(() => import('@/features/programs/pages/edit'));
const ProgramDetailPage = lazy(() => import('@/features/programs/pages/detail'));

// Facility Management - V2 implementation
const FacilityManagement = lazy(() => import('@/features/facilities/pages/index'));
const FacilityDetail = lazy(() => import('@/features/facilities/pages/detail'));
const FacilityEdit = lazy(() => import('@/features/facilities/pages/edit'));
const FacilityNew = lazy(() => import('@/features/facilities/pages/new'));

// Bank Masters Management - V3 implementation
const BankMasterManagement = lazy(() => import('@/features/bank-masters/pages/index'));
const BankMasterDetail = lazy(() => import('@/features/bank-masters/pages/detail'));
const BankMasterEdit = lazy(() => import('@/features/bank-masters/pages/edit'));
const BankMasterNew = lazy(() => import('@/features/bank-masters/pages/new'));

// Label Templates Management - V3 implementation
const LabelTemplateManagement = lazy(() => import('@/features/label-templates/pages/index'));
const LabelTemplateNew = lazy(() => import('@/features/label-templates/pages/new'));
const LabelTemplateEdit = lazy(() => import('@/features/label-templates/pages/edit'));

// Billing Management - V3 implementation
const BillingManagement = lazy(() => import('@/features/billing/pages/BillingManagement'));
const BillingCycleNew = lazy(() => import('@/features/billing/pages/BillingCycleNew'));
const BillingCycleEdit = lazy(() => import('@/features/billing/pages/BillingCycleEdit'));
const BillingCycleDetail = lazy(() => import('@/features/billing/pages/BillingCycleDetail'));
const BillingDashboard = lazy(() => import('@/features/billing/pages/BillingDashboard'));
const CashCollectionList = lazy(() => import('@/features/billing/pages/CashCollectionList'));
const UnpaidMembersList = lazy(() => import('@/features/billing/pages/UnpaidMembersList'));
const TransferResults = lazy(() => import('@/features/billing/pages/TransferResults'));
const ZenginExport = lazy(() => import('@/features/billing/pages/ZenginExport'));

// Billing Adjustments - V3 implementation (統一割引マスタ)
const BillingAdjustmentsList = lazy(() => import('@/features/billing-adjustments/pages/index'));
const BillingAdjustmentDetail = lazy(() => import('@/features/billing-adjustments/pages/detail'));
const BillingAdjustmentNew = lazy(() => import('@/features/billing-adjustments/pages/new'));
const BillingAdjustmentEdit = lazy(() => import('@/features/billing-adjustments/pages/edit'));

// Discount Groups - V3 implementation (グループマスタ)
const DiscountGroupsList = lazy(() => import('@/features/discount-groups/pages/index'));
const DiscountGroupDetail = lazy(() => import('@/features/discount-groups/pages/detail'));
const DiscountGroupNew = lazy(() => import('@/features/discount-groups/pages/new'));
const DiscountGroupEdit = lazy(() => import('@/features/discount-groups/pages/edit'));

// Bulletin Board Management - New Implementation
const BulletinBoardIndex = lazy(() => import('@/features/bulletinBoard/pages/BulletinBoardIndex'));
const BulletinBoardDetail = lazy(() => import('@/features/bulletinBoard/pages/detail'));
const BulletinBoardEdit = lazy(() => import('@/features/bulletinBoard/pages/edit'));

// Chat Management - New Implementation (2カラムレイアウト対応)
const ChatIndex = lazy(() => import('@/features/chat/pages/ChatIndex'));
const ChatRoomNew = lazy(() => import('@/features/chat/pages/ChatRoomNew'));

// Attendance Management - Tablet Attendance System
const TabletAttendanceV2 = lazy(() => import('@/pages/attendance/TabletAttendanceV2'));

// Lane Assignment Management
const ClassLaneAssignment = lazy(
  () => import('@/features/laneAssignment/pages/ClassLaneAssignment')
);
const StudentLaneList = lazy(() => import('@/features/laneAssignment/pages/StudentLaneList'));

// School Management - Grade Dashboard
const GradeDashboard = lazy(() => import('@/features/school-management/pages/GradeDashboard'));
const GradeEvaluationScreen = lazy(
  () => import('@/features/school-management/pages/GradeEvaluationScreen')
);
const KidsAttendanceRecord = lazy(
  () => import('@/features/school-management/pages/KidsAttendanceRecord')
);
const KidsGradeJudgment = lazy(
  () => import('@/features/school-management/pages/KidsGradeJudgment')
);
const MonthlyEvaluationResults = lazy(
  () => import('@/features/school-management/pages/MonthlyEvaluationResults')
);

// Entry-Exit Management - Manual Check-In/Out
const CheckInPage = lazy(() => import('@/features/entry-exit/pages/CheckInPage'));
const CheckOutPage = lazy(() => import('@/features/entry-exit/pages/CheckOutPage'));
const CurrentStatusPage = lazy(() => import('@/features/entry-exit/pages/CurrentStatusPage'));
const AttendanceMonitorPage = lazy(
  () => import('@/features/entry-exit/pages/AttendanceMonitorPage')
);
const AttendanceListPage = lazy(() =>
  import('@/features/entry-exit/pages/AttendanceListPage').then((m) => ({
    default: m.AttendanceListPage,
  }))
);

// Email Broadcasts - V3 implementation
const EmailBroadcastList = lazy(
  () => import('@/features/email-broadcasts/pages/EmailBroadcastList')
);
const EmailBroadcastDetail = lazy(
  () => import('@/features/email-broadcasts/pages/EmailBroadcastDetail')
);
const EmailBroadcastCreate = lazy(
  () => import('@/features/email-broadcasts/pages/EmailBroadcastCreate')
);
const EmailTemplateList = lazy(() => import('@/features/email-broadcasts/pages/EmailTemplateList'));

// Queue Workers Management - V3 implementation
const QueueWorkersPage = lazy(() =>
  import('@/features/queue-workers/pages/index').then((m) => ({ default: m.QueueWorkersPage }))
);

// 進級テスト名簿（下上津役校専用）
const PromotionTestSessionListPage = lazy(
  () => import('@/features/promotion-tests/pages/PromotionTestSessionListPage')
);
const PromotionTestTitleMasterPage = lazy(
  () => import('@/features/promotion-tests/pages/PromotionTestTitleMasterPage')
);
const PromotionTestSessionEditPage = lazy(
  () => import('@/features/promotion-tests/pages/PromotionTestSessionEditPage')
);
const PromotionTestRosterPrintPage = lazy(
  () => import('@/features/promotion-tests/pages/PromotionTestRosterPrintPage')
);

// Reports - V3 implementation
const DailyReport = lazy(() =>
  import('@/features/reports/pages/DailyReport').then((m) => ({ default: m.DailyReport }))
);
const DailyCumulativeReport = lazy(() =>
  import('@/features/reports/pages/DailyCumulativeReport').then((m) => ({
    default: m.DailyCumulativeReport,
  }))
);
const DailyBusinessReport = lazy(() =>
  import('@/features/reports/pages/DailyBusinessReport').then((m) => ({
    default: m.DailyBusinessReport,
  }))
);
const MonthlyBusinessReport = lazy(() =>
  import('@/features/reports/pages/MonthlyBusinessReport').then((m) => ({
    default: m.MonthlyBusinessReport,
  }))
);
const SalesReport = lazy(() =>
  import('@/features/reports/pages/SalesReport').then((m) => ({ default: m.SalesReport }))
);
const SalesTransitionReport = lazy(() =>
  import('@/features/reports/pages/SalesTransitionReport').then((m) => ({
    default: m.SalesTransitionReport,
  }))
);
const MembersReport = lazy(() =>
  import('@/features/reports/pages/MembersReport').then((m) => ({ default: m.MembersReport }))
);
const ProgramsReport = lazy(() =>
  import('@/features/reports/pages/ProgramsReport').then((m) => ({ default: m.ProgramsReport }))
);
const AttendanceReport = lazy(() =>
  import('@/features/reports/pages/AttendanceReport').then((m) => ({ default: m.AttendanceReport }))
);
const StaffReport = lazy(() =>
  import('@/features/reports/pages/StaffReport').then((m) => ({ default: m.StaffReport }))
);
const MemberAchievementReport = lazy(() =>
  import('@/features/reports/pages/MemberAchievementReport').then((m) => ({
    default: m.MemberAchievementReport,
  }))
);
const UserCountReport = lazy(() =>
  import('@/features/reports/pages/UserCountReport').then((m) => ({ default: m.UserCountReport }))
);
const CourseGradeReport = lazy(() =>
  import('@/features/reports/pages/CourseGradeReport').then((m) => ({
    default: m.CourseGradeReport,
  }))
);
const MemberCompositionReport = lazy(() =>
  import('@/features/reports/pages/MemberCompositionReport').then((m) => ({
    default: m.MemberCompositionReport,
  }))
);
const SchoolEnrollmentReport = lazy(() =>
  import('@/features/reports/pages/SchoolEnrollmentReport').then((m) => ({
    default: m.SchoolEnrollmentReport,
  }))
);
const ExcludedMembersSettings = lazy(() =>
  import('@/features/reports/pages/ExcludedMembersSettings').then((m) => ({
    default: m.ExcludedMembersSettings,
  }))
);

// Initialize debug tools if development
initializeDebugTools();

// Create query client with default configurations
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
    mutations: {
      retry: false,
    },
  },
});

// Create theme with Mantine v7 configuration - Fixed globalStyles issue
const theme = createTheme({
  primaryColor: 'blue',
  colors: {
    blue: [
      '#E1F5FE',
      '#B3E5FC',
      '#81D4FA',
      '#4FC3F7',
      '#29B6F6',
      '#03A9F4',
      '#039BE5',
      '#0288D1',
      '#0277BD',
      '#01579B',
    ],
  },
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  radius: {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    xl: '1.5rem',
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 8px 10px rgba(0, 0, 0, 0.04)',
  },
  components: {
    Modal: {
      defaultProps: {
        overlayProps: {
          blur: 6,
        },
        transitionProps: {
          transition: 'fade',
          duration: 200,
        },
      },
    },
    Drawer: {
      defaultProps: {
        overlayProps: {
          blur: 6,
        },
      },
    },
    Card: {
      defaultProps: {
        withBorder: false,
        shadow: 'sm',
      },
      styles: {
        root: {
          border: 'none',
        },
      },
    },
    Paper: {
      defaultProps: {
        withBorder: false,
        shadow: 'sm',
      },
      styles: {
        root: {
          border: 'none',
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DevToolsProvider>
        <MantineProvider theme={theme} defaultColorScheme="light">
          <DatesProvider
            settings={{
              locale: 'ja',
              firstDayOfWeek: 0,
              weekendDays: [0, 6],
              timezone: 'Asia/Tokyo',
            }}
          >
            <Notifications position="top-center" />
            <ModalsProvider
              modalProps={{
                overlayProps: {
                  backgroundOpacity: 0.55,
                  blur: 16,
                },
                transitionProps: {
                  transition: 'fade',
                  duration: 200,
                },
              }}
            >
              <BrowserRouter>
                <Routes>
                  {/* Public routes - Outside all Providers */}
                  <Route
                    path="register"
                    element={
                      <PublicLayout>
                        <Suspense fallback={<LoadingComponent />}>
                          <CustomerPreRegistrationPublicForm />
                        </Suspense>
                      </PublicLayout>
                    }
                  />

                  {/* All other routes - Inside Providers */}
                  <Route
                    path="*"
                    element={
                      <GlassmorphismProvider>
                        <NFCProvider>
                          <AuthProvider>
                            <SidebarProvider>
                              <ThemeProvider>
                                <ErrorBoundaryWrapper>
                                  <NFCLoginHandler />
                                  <CustomerNFCHandler />
                                  <AndroidNFCScanButton />
                                  <Outlet />
                                </ErrorBoundaryWrapper>
                              </ThemeProvider>
                            </SidebarProvider>
                          </AuthProvider>
                        </NFCProvider>
                      </GlassmorphismProvider>
                    }
                  >
                    {/* Login Page */}
                    <Route path="login" element={<Login />} />

                    {/* Dashboard */}
                    <Route
                      path="dashboard"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <EnhancedDashboard />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Bulletin Board Management Routes */}
                    <Route
                      path="bulletin-boards"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BulletinBoardIndex />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="bulletin-boards/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BulletinBoardDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="bulletin-boards/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BulletinBoardEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    {/* Chat Management Routes - 2カラムレイアウト対応 */}
                    <Route
                      path="chat/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ChatRoomNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="chat/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ChatIndex />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="chat"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ChatIndex />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Attendance Management Routes - Tablet System */}
                    <Route
                      path="tablet-attendance-v2"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <TabletAttendanceV2 />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* School Management Routes */}
                    <Route
                      path="school-management/grades"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <GradeDashboard />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="school-management/grade-evaluation"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <GradeEvaluationScreen />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="school-management/kids-attendance"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <KidsAttendanceRecord />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="school-management/grade-judgment"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <KidsGradeJudgment />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="school-management/monthly-evaluation-results"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <MonthlyEvaluationResults />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Lane Assignment Management Routes */}
                    <Route
                      path="lane-assignment/class-lanes"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ClassLaneAssignment />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="lane-assignment/student-lanes"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StudentLaneList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Entry-Exit Management Routes - Manual Check-In/Out */}
                    <Route
                      path="entry-exit/check-in"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CheckInPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="entry-exit/check-out"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CheckOutPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="entry-exit/current-status"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CurrentStatusPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="entry-exit/attendance-list"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <AttendanceListPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="entry-exit/attendance-monitor"
                      element={
                        <PrivateRoute>
                          <Suspense fallback={<LoadingComponent />}>
                            <AttendanceMonitorPage />
                          </Suspense>
                        </PrivateRoute>
                      }
                    />

                    {/* Reports Routes - V3 Implementation */}
                    <Route
                      path="reports/daily"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DailyReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/daily-cumulative"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DailyCumulativeReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/daily-business"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DailyBusinessReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/monthly-business"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <MonthlyBusinessReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/sales"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SalesReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/members"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <MembersReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/programs"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramsReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/attendance"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <AttendanceReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/staff"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StaffReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/member-achievement"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <MemberAchievementReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/excluded-members"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ExcludedMembersSettings />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/user-count"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <UserCountReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/course-grade"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CourseGradeReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/member-composition"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <MemberCompositionReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/school-enrollment"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SchoolEnrollmentReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="reports/sales-transition"
                      element={
                        <PrivateRoute allowedRoles={['admin', 'manager']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SalesTransitionReport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Store Management Routes - V2 Implementation */}
                    <Route
                      path="master/stores"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StoreListPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    {/* 新規作成ページ */}
                    <Route
                      path="master/stores/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StoreNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    {/* 編集ページ */}
                    <Route
                      path="master/stores/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StoreEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/stores/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StoreDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Program Category Management Routes - V2 Implementation */}
                    <Route
                      path="master/program-categories"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramCategoryManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/program-categories/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramCategoryDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/program-categories/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramCategoryNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/program-categories/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramCategoryEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Grade Management Routes - V3 Implementation */}
                    <Route
                      path="master/grades"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <GradeManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/grades/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <GradeDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/grades/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <GradeNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/grades/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <GradeEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Program Management Routes - V2 Implementation */}
                    <Route
                      path="master/programs"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/programs/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/programs/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/programs/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ProgramDetailPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Facility Management Routes - V2 Implementation */}
                    <Route
                      path="master/facilities"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <FacilityManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    {/* 詳細ページは実装済み */}
                    <Route
                      path="master/facilities/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <FacilityDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/facilities/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <FacilityNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/facilities/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <FacilityEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Staff Management Routes - V3 Implementation */}
                    <Route
                      path="master/staffs"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StaffManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/staffs/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StaffDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/staffs/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StaffEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/staffs/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <StaffNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Customer Management Routes - V3 Implementation */}
                    <Route
                      path="customers"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    {/* 🔧 FIX: /new を :id より前に配置（具体的なパスを先にマッチ） */}
                    <Route
                      path="customers/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    {/* 再入会コード作成 */}
                    <Route
                      path="customers/rejoin"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerRejoin />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="customers/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="customers/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Customer Pre-Registration Management Routes - V3 Implementation */}
                    <Route
                      path="customer-pre-registrations"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerPreRegistrationList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="customer-pre-registrations/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerPreRegistrationEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="customer-pre-registrations/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CustomerPreRegistrationDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Email Broadcasts Routes - V3 Implementation */}
                    <Route
                      path="email-broadcasts"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <EmailBroadcastList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="email-broadcasts/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <EmailBroadcastCreate />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="email-broadcasts/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <EmailBroadcastCreate />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="email-broadcasts/templates"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <EmailTemplateList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="email-broadcasts/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <EmailBroadcastDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Billing Management Routes - V3 Implementation */}
                    <Route
                      path="billing"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/cycles/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingCycleNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/cycles/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingCycleEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/cycles/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingCycleDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/dashboard"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingDashboard />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/cash-collections"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <CashCollectionList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/unpaid"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <UnpaidMembersList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/cycles/:id/transfer-results"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <TransferResults />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/zengin-export"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <ZenginExport />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Billing Adjustments Routes - 統一割引マスタ */}
                    <Route
                      path="billing/adjustments"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingAdjustmentsList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/adjustments/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingAdjustmentNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/adjustments/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingAdjustmentEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="billing/adjustments/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BillingAdjustmentDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Discount Groups Routes - グループマスタ */}
                    <Route
                      path="discount-groups"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DiscountGroupsList />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="discount-groups/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DiscountGroupNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="discount-groups/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DiscountGroupEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="discount-groups/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <DiscountGroupDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* School Management Routes - V3 Implementation */}
                    <Route
                      path="master/schools"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SchoolManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/schools/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SchoolDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/schools/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SchoolEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/schools/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <SchoolNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Bank Masters Management Routes - V3 Implementation */}
                    <Route
                      path="master/banks"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BankMasterManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/banks/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BankMasterDetail />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/banks/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BankMasterEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/banks/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <BankMasterNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Label Templates Management Routes - V3 Implementation */}
                    <Route
                      path="master/label-templates"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <LabelTemplateManagement />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/label-templates/new"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <LabelTemplateNew />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="master/label-templates/:id/edit"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <LabelTemplateEdit />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Queue Workers Management Routes - V3 Implementation (System) */}
                    <Route
                      path="master/system/workers"
                      element={
                        <PrivateRoute allowedRoles={['admin']}>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <QueueWorkersPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* 進級テスト名簿ルート（下上津役校専用） */}
                    <Route
                      path="promotion-tests"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <PromotionTestSessionListPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="promotion-tests/titles"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <PromotionTestTitleMasterPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="promotion-tests/:id"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <PromotionTestSessionEditPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="promotion-tests/:id/print"
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Suspense fallback={<LoadingComponent />}>
                              <PromotionTestRosterPrintPage />
                            </Suspense>
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Default redirect */}
                    <Route
                      path=""
                      element={
                        <PrivateRoute>
                          <MainLayout>
                            <Navigate to="/dashboard" replace />
                          </MainLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Fallback route */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </Routes>
              </BrowserRouter>
            </ModalsProvider>
          </DatesProvider>
        </MantineProvider>
      </DevToolsProvider>
    </QueryClientProvider>
  );
}

export default App;

// Mount the application
const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
